import { useRef, useState } from "react";
import type { Msg, OpenAiUsage } from "../types";
import type { ChatbotBlock } from "blocks/v2/Chatbot/schema";
import { calculateOpenAiUsage } from "blocks/v2/Chatbot/calculateOpenAiUsage";
import { fetchWithProjectSession } from "lib/project-sessions/fetch";
import { z } from "zod";
import { useChatbotSessionId } from "../ChatbotSessionIdContext";

export type Payload = {
  messages: Msg[];
  blockId: string;
};

export type Status = "idle" | "loading" | "error" | "complete";
export type CompletionStatus = {
  status: Status;
  code: string;
};

const ErrorResponseSchema = z
  .object({
    code: z.string(),
    error: z.string(),
    meta: z
      .object({
        code: z.string(),
        message: z.string(),
        param: z.string(),
        type: z.string(),
      })
      .partial()
      .passthrough()
      .optional(),
  })
  .passthrough();

const CODE_FOR_USER = Symbol("interfaces-chat-error-code");
const chatError = function (message: string, code: string) {
  const error = Error(message);
  Object.defineProperty(error, CODE_FOR_USER, {
    get() {
      return code;
    },
  });
  // Omit frames above userError
  if (Error.captureStackTrace) Error.captureStackTrace(error, chatError);
  return error;
};
const getChatErrorCode = (e: any) => e[CODE_FOR_USER];

export function useChatCompletion(
  model: ChatbotBlock["config"]["model"],
  onUsage: (usage: OpenAiUsage) => void,
  onResultUpdated?: (result: string) => void
) {
  const chatbotSessionId = useChatbotSessionId();

  const resultRef = useRef("");
  // TODO pull this state up a level as part of container work to avoid need for onResultUpdated
  // https://zapierorg.atlassian.net/browse/INTRFCS-1124
  const [result, setResult] = useState("");

  async function runChatCompletion(
    payload: Payload
  ): Promise<CompletionStatus> {
    try {
      setResult(() => "");
      onResultUpdated && onResultUpdated("");
      resultRef.current = "";

      const controller = new AbortController();
      const { signal } = controller;

      // TODO: zap function calls can take awhile, this timeout may need to
      // be increased in some cases, perhaps we explore some UX that would
      // allow longer timeout without too much pain?
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetchWithProjectSession("/api/chat/v2", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          ...payload,
          chatbotSessionId,
        }),
        signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorResponse = await response.json();

        const parsedErrorResponse =
          ErrorResponseSchema.safeParse(errorResponse);

        if (parsedErrorResponse.success) {
          throw chatError(
            parsedErrorResponse.data.meta?.message ??
              parsedErrorResponse.data.error,
            parsedErrorResponse.data.meta?.code ?? parsedErrorResponse.data.code
          );
        }

        throw chatError(
          response.statusText,
          response.statusText.toLowerCase().replace(/\s/g, "_")
        );
      }

      const data = response.body;

      if (!data) {
        throw chatError("No response was generated.", "no_data");
      }

      const reader = data.getReader();
      const decoder = new TextDecoder();

      let done = false;

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;

        const decodedValue = decoder.decode(value);

        if (decodedValue) {
          resultRef.current = resultRef.current + decodedValue;
          setResult((prev) => {
            const res = prev + decodedValue;
            onResultUpdated && onResultUpdated(res);
            return res;
          });
        }
      }

      onUsage(calculateOpenAiUsage(model, payload.messages, resultRef.current));

      if (/\[max_completion_limit\]$/.test(resultRef.current)) {
        resultRef.current = "";
        throw chatError(
          "You've reached the chat completion limit for this session.",
          "max_completion_limit"
        );
      }

      return {
        status: "complete",
        code: "success",
      };
    } catch (error) {
      const isAbortError =
        error instanceof DOMException && error.name === "AbortError";

      const code =
        (isAbortError ? "timeout" : getChatErrorCode(error)) ?? "unknown";

      resultRef.current = isAbortError
        ? "Hmm, things are taking a bit too long. You can try again, or try a different question."
        : "Something went wrong.";

      return {
        status: "error",
        code,
      };
    }
  }

  return [runChatCompletion, resultRef, result] as const;
}
